import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../components/layout/Layout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import ErrorBoundary from '../components/common/ErrorBoundary';
import Login from '../features/auth/Login';
import CompletePOSScreen from '../features/cashier/CompletePOSScreen';
import ModernPOSScreen from '../features/cashier/ModernPOSScreen';
import ItemList from '../features/inventory/ItemList';
import AddItemPage from '../features/inventory/AddItemPage';
import EditItemPage from '../features/inventory/EditItemPage';
import StockManagement from '../features/inventory/StockManagement';
import StockOpname from '../features/inventory/StockOpname';
import PurchaseOrderList from '../features/purchase-orders/PurchaseOrderList';
import CompleteMutationPage from '../features/mutations/CompleteMutationPage';
import CompleteReturnsPage from '../features/returns/CompleteReturnsPage';
import DailySummary from '../features/reports/DailySummary';
import ReportsDashboard from '../features/reports/ReportsDashboard';
import PublicCatalog from '../features/catalog/PublicCatalog';
import BranchList from '../features/branch/BranchList';
import CustomerListPage from '../features/customer/CustomerListPage';
import ProfilePage from '../features/users/ProfilePage';
import UserManagementPage from '../features/users/UserManagementPage';
import ActivityLogsPage from '../features/activity/ActivityLogsPage';
import BackupManagementPage from '../features/backup/BackupManagementPage';
import PrintSettingsPage from '../features/settings/PrintSettingsPage';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import OfflineBanner from '../components/OfflineBanner';
import NetworkErrorRecovery from '../components/NetworkErrorRecovery';
import { useDisablePullToRefresh, usePersistAuth } from '../hooks/usePWAFixes';
import { setUser } from '../features/auth/authSlice';
import { AppDispatch, RootState } from '../store/store';
import { enhancedIndexedDBService } from '../services/enhancedIndexedDBService';
import { indexedDBService } from '../services/indexedDBService';  // Also init the standard service
import { enhancedSyncService } from '../services/enhancedSyncService';

const App = () => {
  // Disable pull-to-refresh and overscroll
  useDisablePullToRefresh();
  
  // Persist auth state
  usePersistAuth();
  
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [servicesInitialized, setServicesInitialized] = useState(false);

  // Initialize enhanced offline services on app startup
  useEffect(() => {
    const initServices = async () => {
      try {
        console.log('🚀 Initializing enhanced offline services...');
        
        // Initialize BOTH IndexedDB services
        await Promise.all([
          indexedDBService.init(),
          enhancedIndexedDBService.init()
        ]);
        console.log('✅ IndexedDB services initialized');
        
        // Initialize sync service
        await enhancedSyncService.init();
        console.log('✅ Enhanced Sync Service initialized');
        
        // Start auto-sync (every 2 minutes)
        enhancedSyncService.startAutoSync(120000); // 2 minutes
        console.log('✅ Auto-sync started (2 min intervals)');
        
        setServicesInitialized(true);
        console.log('🎉 All enhanced services ready!');
      } catch (error) {
        console.error('❌ Failed to initialize services:', error);
        // App still works, just without enhanced offline features
      }
    };

    initServices();

    // Cleanup on unmount
    return () => {
      enhancedSyncService.stopAutoSync();
    };
  }, []);

  // Restore user from localStorage on mount
  useEffect(() => {
    if (!isAuthenticated) {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          dispatch(setUser(user));
        } catch (error) {
          console.error('Failed to restore user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
    }
  }, [dispatch, isAuthenticated]);

  return (
    <ErrorBoundary>
      <Router>
        <NetworkErrorRecovery />
        <OfflineBanner />
        <PWAInstallPrompt />
        <Routes>
          {/* Public routes - No authentication required */}
          <Route path="/login" element={<Login />} />
          <Route path="/catalog" element={<PublicCatalog />} />
        
        {/* Protected routes - Require authentication */}
        <Route path="/" element={
          <ProtectedRoute>
            <ModernPOSScreen />
          </ProtectedRoute>
        } />
        <Route path="/pos" element={
          <ProtectedRoute>
            <ModernPOSScreen />
          </ProtectedRoute>
        } />
        <Route path="/pos/modern" element={
          <ProtectedRoute>
            <ModernPOSScreen />
          </ProtectedRoute>
        } />
        <Route path="/pos/classic" element={
          <ProtectedRoute>
            <Layout>
              <CompletePOSScreen />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/cashier" element={
          <ProtectedRoute>
            <ModernPOSScreen />
          </ProtectedRoute>
        } />
        <Route path="/inventory" element={
          <ProtectedRoute>
            <Layout>
              <ItemList />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/add" element={
          <ProtectedRoute>
            <AddItemPage />
          </ProtectedRoute>
        } />
        <Route path="/inventory/edit/:id" element={
          <ProtectedRoute>
            <EditItemPage />
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute>
            <Layout>
              <CustomerListPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/stock" element={
          <ProtectedRoute>
            <Layout>
              <StockManagement />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/opname" element={
          <ProtectedRoute>
            <Layout>
              <StockOpname />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/purchase-orders" element={
          <ProtectedRoute>
            <Layout>
              <PurchaseOrderList />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/mutations" element={
          <ProtectedRoute>
            <Layout>
              <CompleteMutationPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/returns" element={
          <ProtectedRoute>
            <Layout>
              <CompleteReturnsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Layout>
              <ReportsDashboard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/branches" element={
          <ProtectedRoute>
            <Layout>
              <BranchList />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Profile - All authenticated users */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        
        {/* Print Settings - All authenticated users */}
        <Route path="/print-settings" element={
          <ProtectedRoute>
            <PrintSettingsPage />
          </ProtectedRoute>
        } />
        
        {/* User Management - Owner and Admin only */}
        <Route path="/users" element={
          <ProtectedRoute roles={['owner', 'admin']}>
            <Layout>
              <UserManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Activity Logs - Owner only */}
        <Route path="/activity-logs" element={
          <ProtectedRoute roles={['owner']}>
            <ActivityLogsPage />
          </ProtectedRoute>
        } />
        
        {/* Backup Management - Owner and Admin only */}
        <Route path="/backups" element={
          <ProtectedRoute roles={['owner', 'admin']}>
            <Layout>
              <BackupManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Redirect unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </ErrorBoundary>
  );
};

export default App;